import { Injectable, signal, computed, effect } from '@angular/core';

/**
 * Service to manage sidebar navigation state with Angular signals
 *
 * Features:
 * - Reactive state management with signals
 * - Sidebar expanded/collapsed state (icons only vs full menu)
 * - Sidebar pinned state (stays expanded vs auto-collapse)
 * - Responsive detection (mobile vs desktop)
 * - LocalStorage persistence for pinned state
 * - Automatic migration from legacy localStorage key
 *
 * @example
 * ```typescript
 * // Inject the service
 * private drawerStateService = inject(DrawerStateService);
 *
 * // Access state signals
 * expanded = this.drawerStateService.expanded;
 * pinned = this.drawerStateService.pinned;
 * isMobile = this.drawerStateService.isMobile;
 *
 * // Control sidebar
 * this.drawerStateService.expand();
 * this.drawerStateService.collapse();
 * this.drawerStateService.togglePin();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class DrawerStateService {
  // LocalStorage keys
  private readonly PINNED_KEY = 'drawer-pinned';
  private readonly LEGACY_KEY = 'static-menu-active';

  // Responsive breakpoint (matches existing isMobile logic)
  private readonly MOBILE_BREAKPOINT = 991;

  // Debounce timer for resize events
  private resizeDebounceTimer: number | null = null;
  private readonly RESIZE_DEBOUNCE_MS = 250;

  // Resize event listener reference for cleanup
  private resizeListener: (() => void) | null = null;

  // Private state signals
  private _expanded = signal<boolean>(false); // Sidebar expanded (shows labels) vs collapsed (icons only)
  private _pinned = signal<boolean>(this.loadPinnedState()); // Sidebar stays expanded vs auto-collapse
  private _isMobile = signal<boolean>(this.checkMobile());

  // Public readonly signals
  readonly expanded = this._expanded.asReadonly();
  readonly pinned = this._pinned.asReadonly();
  readonly isMobile = this._isMobile.asReadonly();

  // Legacy compatibility - map expanded to visible
  readonly visible = this.expanded;

  /**
   * Computed signal for sidebar width
   * - Expanded: 280px (shows icons + labels)
   * - Collapsed: 60px (shows icons only)
   * - Mobile: Full overlay
   */
  readonly sidebarWidth = computed(() => {
    if (this.isMobile()) {
      return '280px';
    }
    return this.expanded() ? '280px' : '60px';
  });

  /**
   * Computed signal for sidebar CSS classes
   */
  readonly sidebarClass = computed(() => ({
    'sidebar-expanded': this.expanded(),
    'sidebar-collapsed': !this.expanded(),
    'sidebar-pinned': this.pinned() && !this.isMobile(),
    'sidebar-mobile': this.isMobile()
  }));

  constructor() {
    // Set up effect to persist pinned state to localStorage
    effect(() => {
      const pinnedValue = this.pinned();
      localStorage.setItem(this.PINNED_KEY, String(pinnedValue));
      this.logInfo(`Pinned state persisted: ${pinnedValue}`);
    });

    // Auto-expand if pinned on desktop
    effect(() => {
      if (this.pinned() && !this.isMobile()) {
        this._expanded.set(true);
      }
    });
  }

  /**
   * Initialize responsive detection with window resize listener
   * Should be called in ngOnInit of the main layout component
   */
  initializeResponsiveDetection(): void {
    if (typeof window === 'undefined') {
      return; // Skip in SSR environment
    }

    // Create resize listener with debounce
    this.resizeListener = () => {
      if (this.resizeDebounceTimer !== null) {
        window.clearTimeout(this.resizeDebounceTimer);
      }

      this.resizeDebounceTimer = window.setTimeout(() => {
        this.setMobile(this.checkMobile());
        this.resizeDebounceTimer = null;
      }, this.RESIZE_DEBOUNCE_MS);
    };

    // Add event listener
    window.addEventListener('resize', this.resizeListener);

    // Initial check
    this.setMobile(this.checkMobile());

    this.logInfo('Responsive detection initialized');
  }

  /**
   * Toggle sidebar expanded state
   */
  toggle(): void {
    this._expanded.set(!this._expanded());
    this.logInfo(
      `Sidebar toggled: ${this._expanded() ? 'expanded' : 'collapsed'}`
    );
  }

  /**
   * Expand the sidebar (show labels)
   */
  expand(): void {
    if (!this._expanded()) {
      this._expanded.set(true);
      this.logInfo('Sidebar expanded');
    }
  }

  /**
   * Collapse the sidebar (icons only)
   */
  collapse(): void {
    if (this._expanded()) {
      this._expanded.set(false);
      this.logInfo('Sidebar collapsed');
    }
  }

  // Legacy compatibility methods
  open(): void {
    this.expand();
  }

  close(): void {
    this.collapse();
  }

  /**
   * Toggle sidebar pinned state (desktop only)
   * On mobile, this has no effect
   */
  togglePin(): void {
    if (this.isMobile()) {
      this.logWarn('Cannot toggle pin on mobile');
      return;
    }

    this._pinned.set(!this._pinned());
    this.logInfo(
      `Sidebar pin toggled: ${this._pinned() ? 'pinned' : 'unpinned'}`
    );
  }

  /**
   * Set mobile state
   * @param isMobile Whether the viewport is mobile-sized
   */
  setMobile(isMobile: boolean): void {
    if (this._isMobile() !== isMobile) {
      this._isMobile.set(isMobile);
      this.logInfo(`Mobile state changed: ${isMobile}`);

      // Collapse sidebar when switching to mobile
      if (isMobile && this._expanded()) {
        this.collapse();
      }
    }
  }

  /**
   * Clean up event listeners
   * Should be called in ngOnDestroy of the main layout component
   */
  cleanup(): void {
    if (this.resizeListener && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
      this.logInfo('Responsive detection cleaned up');
    }

    if (this.resizeDebounceTimer !== null) {
      window.clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = null;
    }
  }

  /**
   * Load pinned state from localStorage
   * Migrates from legacy key if needed
   * @private
   */
  private loadPinnedState(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false; // Default to unpinned in SSR
    }

    // Check for new key first
    const pinnedValue = localStorage.getItem(this.PINNED_KEY);
    if (pinnedValue !== null) {
      return pinnedValue === 'true';
    }

    // Migrate from legacy key
    const legacyValue = localStorage.getItem(this.LEGACY_KEY);
    if (legacyValue !== null) {
      const migratedValue = legacyValue === 'true';
      localStorage.setItem(this.PINNED_KEY, String(migratedValue));
      localStorage.removeItem(this.LEGACY_KEY);
      this.logInfo(`Migrated pinned state from legacy key: ${migratedValue}`);
      return migratedValue;
    }

    // Default to unpinned
    return false;
  }

  /**
   * Check if viewport is mobile-sized
   * @private
   */
  private checkMobile(): boolean {
    if (typeof window === 'undefined') {
      return false; // Default to desktop in SSR
    }

    return window.innerWidth <= this.MOBILE_BREAKPOINT;
  }

  /**
   * Log an info message with colors
   * @private
   */
  private logInfo(message: string): void {
    console.info(
      `%c[DrawerState] %c${message}`,
      'color: #6366f1',
      'color: #777'
    );
  }

  /**
   * Log a warning message with colors
   * @private
   */
  private logWarn(message: string): void {
    console.warn(
      `%c[DrawerState] %c${message}`,
      'color: #6366f1',
      'color: #ff9800'
    );
  }
}
